import { useState, useEffect } from "react";
import { creamProducts } from "@/data/products";
import { goToShopify } from "@/lib/shopify";

/**
 * Lookbook — cream editorial moments.
 * 4 large product portraits with cinematic captions, no broken external videos.
 */
const items = [
  {
    product: creamProducts["embossed-cream-unixorn-set"],
    label: "THE COMPLETE FORM",
    subtitle: "Worn together. Walked forward.",
  },
  {
    product: creamProducts["saint-tee"],
    label: "SAINT OF ORIGINALITY",
    subtitle: "A relic for the forward-walking few.",
  },
  {
    product: creamProducts["sigil-tee"],
    label: "THE SIGIL",
    subtitle: "Carried, never explained.",
  },
  {
    product: creamProducts["unixorn-relic-bucket-hat"],
    label: "THE CROWN",
    subtitle: "Worn at the top.",
  },
];

const LookbookTile = ({ item, index }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setIsVisible(true)),
      { threshold: 0.15 }
    );
    const el = document.getElementById(`lookbook-tile-${item.product.id}`);
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, [item.product.id]);

  return (
    <div
      id={`lookbook-tile-${item.product.id}`}
      onClick={() => goToShopify(item.product.id)}
      className={`relative aspect-square overflow-hidden bg-[#F5F0E5] cursor-pointer group transition-all duration-1000 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
      data-testid={`lookbook-tile-${item.product.id}`}
    >
      <img
        src={item.product.image}
        alt={item.product.name}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/[0.03] transition-colors duration-500" />

      <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9 bg-gradient-to-t from-[#F4EFE6]/95 via-[#F4EFE6]/60 to-transparent">
        <p className="text-stone-900 text-sm md:text-base tracking-[0.3em] uppercase font-semibold">
          {item.label}
        </p>
        <p className="text-stone-600 text-xs md:text-sm tracking-wide italic mt-1.5">
          {item.subtitle}
        </p>
      </div>
    </div>
  );
};

export const LookbookSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setIsVisible(true)),
      { threshold: 0.1 }
    );
    const el = document.getElementById("lookbook");
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="lookbook" className="bg-[#F4EFE6] relative overflow-hidden">
      {/* Editorial header */}
      <div className="section-padding-sm border-y border-stone-300/60">
        <div className="container-luxury">
          <div
            className={`max-w-3xl mx-auto text-center transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-[11px] tracking-[0.4em] uppercase text-stone-500 mb-5">
              UNIXORN — Lookbook
            </p>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-stone-900 tracking-tight mb-6">
              THE UNICORN IN STILLNESS
            </h2>
            <p className="text-base md:text-lg text-stone-600 tracking-wide italic">
              Each frame a relic. Each piece a forward step.
            </p>
          </div>
        </div>
      </div>

      {/* Editorial grid */}
      <div className="section-padding bg-[#EBE3D2] relative">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 max-w-6xl mx-auto">
            {items.map((item, idx) => (
              <LookbookTile key={item.product.id} item={item} index={idx} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LookbookSection;
